import { shortCurrencyFormatter } from 'components/KPIs/helpers';
import React from 'react';
import ReactApexChart from 'react-apexcharts';

/**
 * A grouped bar chart that displays revenue and profit per team.
 * A horizontal line marker is added on the profit bar for each team to indicate the profit goal.
 *
 * Expected output from valueExtractor(teams):
 * {
 *   teamLabels: Array<string>, // team names
 *   profitGoalData: Array<number>, // profit goal per team (for annotations)
 *   teamSeries: Array<{ name: string, data: Array<number> }> // series for revenue and profit
 * }
 */
export const groupedMetricsValueExtractor = (teams) => {
    // X-axis: team names
    const teamLabels = teams.map(team => team.name);
    // Y-values for each metric
    const revenueData = teams.map(team => team.income);
    const profitData = teams.map(team => team.gross_profit);
    const profitGoalData = teams.map(team => team.gross_profit_goal);

    return {
        teamLabels,
        profitGoalData, // extra field for annotations
        teamSeries: [
            { name: 'Revenue', data: revenueData },
            { name: 'Profit', data: profitData },
        ],
    };
};

const GroupedTeamBarChart = ({
    teams,
    valueExtractor,
    defaultFormatter = shortCurrencyFormatter,
}) => {
    // Extract data using the provided valueExtractor.
    const { teamLabels, teamSeries, profitGoalData } = valueExtractor(teams);

    // Define custom colors for the two metrics.
    const metricColors = ['#ffc107', '#28a745']; // Revenue, Profit

    // Generate annotations: one point per team showing the profit goal.
    // Here we replace the default marker with a custom HTML element that renders a horizontal line.
    const annotationsPoints = teamLabels.map((label, index) => ({
        x: label,
        y: profitGoalData[index],
        marker: {
            shape: "line",
            fillColor: '#dc3545',
            strokeWidth: 3,
            size: 20,
            offsetX: 20,
        },
        label: {
            text: shortCurrencyFormatter(profitGoalData[index]),
            style: {
                // color: '#dc3545',
                background: '#fff',
                fontSize: '8px',
            },
            offsetY: 6,
            offsetX: 20,
        },
    }));

    // Chart options for a grouped bar chart.
    const options = {
        chart: {
            type: 'bar',
            animations: { enabled: false },
        },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '55%',
                distributed: false,
            },
        },
        colors: metricColors,
        dataLabels: {
            enabled: false,
            style: {
                fontWeight: 500,
            },
        },
        xaxis: {
            categories: teamLabels,
            labels: {
                formatter: (val) => val,
                style: {
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    fontWeight: 500,
                },
            },
        },
        yaxis: {
            labels: {
                formatter: (val) => defaultFormatter(val),
                style: {
                    fontSize: '14px',
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    fontWeight: 600,
                },
            },
        },
        tooltip: {
            y: {
                formatter: (val) => defaultFormatter(val),
            },
        },
        legend: {
            position: 'top',
            horizontalAlign: 'left',
            fontWeight: 600,
        },
        annotations: {
            points: annotationsPoints,
        },
    };

    return (
        <div style={{ width: '100%', margin: '0 auto', height: '500px' }}>
            <ReactApexChart options={options} series={teamSeries} type="bar" height={480} />
        </div>
    );
};

export default GroupedTeamBarChart;
